import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

export type LocationSearchAddress = {
  municipality?: string;
  countrySubdivision?: string;
  country?: string;
};

export type LocationSearchResult = {
  address?: LocationSearchAddress;
};

type LocationSearchState = {
  loading: boolean;
  error: boolean;
  errorMessage: string;
  results: LocationSearchResult[];
  query: string;
};

const initialState: LocationSearchState = {
  loading: false,
  error: false,
  errorMessage: '',
  results: [],
  query: '',
};

export const getLocationSearchResults = createAsyncThunk<
  { query: string; results: LocationSearchResult[] },
  string
>('locationSearch/getResults', async (query) => {
  const trimmed = query.trim();
  if (!trimmed) {
    return { query: trimmed, results: [] };
  }
  const { data } = await axios.get(
    `https://atlas.microsoft.com/search/address/json?api-version=1.0&entityType=Municipality&query=${encodeURIComponent(
      trimmed
    )}&subscription-key=cTbC9G2Tkk76cfNABIKl5rrAdh0K5_PP_1pmc1zCN3Y`
  );
  const results = Array.isArray(data?.results) ? data.results : [];
  return { query: trimmed, results };
});

const locationSearchSlice = createSlice({
  name: 'locationSearch',
  initialState,
  reducers: {
    resetLocationSearch: (state) => {
      state.loading = false;
      state.error = false;
      state.errorMessage = '';
      state.results = [];
      state.query = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLocationSearchResults.pending, (state, action) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
      state.query = action.meta.arg.trim();
    });
    builder.addCase(getLocationSearchResults.fulfilled, (state, action) => {
      if (action.payload.query !== state.query) {
        return;
      }
      state.loading = false;
      state.results = action.payload.results;
    });
    builder.addCase(getLocationSearchResults.rejected, (state, action) => {
      if (action.meta.arg.trim() !== state.query) {
        return;
      }
      state.loading = false;
      state.error = true;
      state.errorMessage =
        action.error.message ?? 'Failed to load location results.';
      state.results = [];
    });
  },
});

export const { resetLocationSearch } = locationSearchSlice.actions;
export default locationSearchSlice.reducer;
